import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import useServices from '../../hooks/useServices';
import './Services.css';

const Services = () => {
  const [activeTab, setActiveTab] = useState('commercial');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { services, loading } = useServices();
  const tabTimeoutRef = useRef(null);
  const slideIntervalRef = useRef(null);

  const tabs = [
    { id: 'commercial', label: 'Commercial' },
    { id: 'residential', label: 'Residential' },
    { id: 'industrial', label: 'Industrial' }
  ];

  const filteredServices = services.filter(service => service.category === activeTab);
  const totalSlides = Math.ceil(filteredServices.length / 2) || 1;
  const maxSlide = totalSlides - 1;

  const nextTab = useCallback(() => {
    setActiveTab(prev => {
      const currentIndex = tabs.findIndex(t => t.id === prev);
      return tabs[(currentIndex + 1) % tabs.length].id;
    });
    setCurrentSlide(0);
  }, [tabs]);

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    
    // If this is the last slide, move to next tab
    if (currentSlide >= maxSlide) {
      nextTab();
      return;
    }
    
    setIsTransitioning(true);
    setCurrentSlide(prev => prev + 1);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, maxSlide, currentSlide, nextTab]);

  const prevSlide = useCallback(() => {
    if (isTransitioning || filteredServices.length <= 2) return;
    setIsTransitioning(true);
    setCurrentSlide(prev => (prev <= 0 ? maxSlide : prev - 1));
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, maxSlide, filteredServices.length]);

  // Reset slide when tab changes
  useEffect(() => { setCurrentSlide(0); }, [activeTab]);

  // Auto-advance slides - when all slides shown, go to next tab
  useEffect(() => {
    if (filteredServices.length === 0) {
      // No services in this tab, skip to next
      nextTab();
      return;
    }
    
    if (filteredServices.length <= 2 && totalSlides <= 1) {
      // Only one slide, wait then go to next tab
      tabTimeoutRef.current = setTimeout(nextTab, 5000);
      return () => clearTimeout(tabTimeoutRef.current);
    }
    
    // Multiple slides, auto-rotate
    slideIntervalRef.current = setInterval(nextSlide, 4000);
    return () => clearInterval(slideIntervalRef.current);
  }, [nextSlide, nextTab, filteredServices.length, totalSlides]);

  if (loading) {
    return (
      <section className="services-section">
        <div className="services-container">
          <div className="services-header">
            <h4 className="services-subtitle">Our Services</h4>
            <h1 className="services-title">The Best Service For You</h1>
            <p className="services-description">Loading services...</p>
          </div>
        </div>
      </section>
    );
  }

  if (services.length === 0) {
    return (
      <section className="services-section">
        <div className="services-container">
          <div className="services-header">
            <h4 className="services-subtitle">Our Services</h4>
            <h1 className="services-title">The Best Service For You</h1>
            <p className="services-description">No services available at the moment.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="services-section">
      <div className="services-container">
        <div className="services-header">
          <h4 className="services-subtitle">Our Services</h4>
          <h1 className="services-title">The Best Service For You</h1>
          <p className="services-description">
            We provide comprehensive electrical solutions tailored to your needs.
            From installations to repairs, our expert team delivers quality service
            for residential, commercial, and industrial clients.
          </p>
        </div>

        <div className="services-content">
          <div className="services-tabs">
            <button className="carousel-nav carousel-prev desktop-nav-arrow" onClick={prevSlide} aria-label="Previous">❮</button>
            <ul className="tabs-nav">
              {tabs.map((tab) => (
                <li key={tab.id} className="tab-item">
                  <button className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`} onClick={() => { setActiveTab(tab.id); setCurrentSlide(0); }}>
                    <span>{tab.label}</span>
                  </button>
                </li>
              ))}
            </ul>
            <button className="carousel-nav carousel-next desktop-nav-arrow" onClick={nextSlide} aria-label="Next">❯</button>
          </div>

          <div className="services-carousel">
            {filteredServices.length > 0 ? (
              <div className="carousel-track" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                  <div className="carousel-slide" key={slideIndex}>
                    {filteredServices.slice(slideIndex * 2, slideIndex * 2 + 2).map((service) => (
                      <Link to={`/services/${service.slug}`} className="home-service-card" key={service.id}>
                        <img
                          src={service.image}
                          alt={service.name}
                          className="home-service-img"
                          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=400&q=80'; }}
                        />
                        <div className="home-service-info">
                          <h4>{service.name}</h4>
                          <p>{service.description}</p>
                          <span className="home-service-btn">{service.buttonText}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-services-msg">
                <p>No services in this category yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
